import PolicyPage, { PolicySection, PolicyList } from 'components/compliance/policy-page'
import compliance from 'data/compliance.json'

const { contact, complaints } = compliance
const { owner, timescales, escalationContact } = complaints

function Complaints() {
  return (
    <PolicyPage
      title='Complaints Policy'
      description='How to make a complaint to UHDSS, who reviews it, how long we aim to take, how to escalate, and when a complaint belongs to a partner institution.'
      intro='If you are not happy with something we have done, please tell us. We treat complaints as a way to put things right and to improve how we work.'
    >
      <PolicySection title='How to make a complaint'>
        <p className='dark-blue fs-6'>
          You can raise a complaint through any of the contact channels below. Writing to us by
          email is usually the quickest way, because it gives us a written record from the start.
        </p>
        <ul className='dark-blue fs-6'>
          <li className='mb-2'>
            Email:{' '}
            <a className='text-decoration-none' href={`mailto:${owner.email}?subject=Complaint`}>{owner.email}</a>
          </li>
          <li className='mb-2'>
            Telephone:{' '}
            <a className='text-decoration-none' href={`tel:${contact.phone.replace(/\s/g, '')}`}>{contact.phone}</a>
          </li>
          <li className='mb-2'>
            In person or by post at our head office:{' '}
            {contact.headOffice.join(', ')}
          </li>
        </ul>
        <p className='dark-blue fs-6'>
          Where you can, please include:
        </p>
        <PolicyList items={[
          'Your full name and how we can contact you',
          'Your UHDSS application or reference number, if you have one',
          'A clear description of what happened, with the relevant dates',
          'The names of any staff involved, if you know them',
          'Any documents that support your complaint',
          'What you would like us to do to put things right'
        ]} />
      </PolicySection>

      <PolicySection title='Who reviews your complaint'>
        <p className='dark-blue fs-6'>
          Complaints are owned by the {owner.role}. Your complaint will be assigned to a member of
          management who was not directly responsible for the matter you are complaining about,
          wherever that is reasonably possible.
        </p>
      </PolicySection>

      <PolicySection title='Acknowledgement and response'>
        <p className='dark-blue fs-6'>
          We aim to acknowledge a complaint within {timescales.acknowledgementWorkingDays} working
          days of receiving it, and to give you a substantive response within{' '}
          {timescales.responseWorkingDays} working days.
        </p>
        <p className='dark-blue fs-6'>
          If a complaint is complicated, or if we need information from an institution or another
          third party, it may take longer. In that case we will tell you, explain why, and give you
          a new expected date for our response. These are the targets we work to, not a legal
          guarantee.
        </p>
      </PolicySection>

      <PolicySection title='If you are not satisfied'>
        <p className='dark-blue fs-6'>
          If our response does not resolve the matter, you can ask for your complaint to be
          escalated to {escalationContact.role.toLowerCase()} by writing to{' '}
          <a className='text-decoration-none' href={`mailto:${escalationContact.email}?subject=Complaint escalation`}>{escalationContact.email}</a>.
          Please explain what our first response did not address. We aim to review an escalated
          complaint within {timescales.escalationWorkingDays} working days.
        </p>
      </PolicySection>

      <PolicySection title='Complaints about a university or institution'>
        <p className='dark-blue fs-6'>
          Some matters are decided by the institution rather than by us. Academic decisions,
          admission decisions, tuition disputes and institutional policies normally fall to the
          university or college concerned, and they have their own complaints procedures.
        </p>
        <p className='dark-blue fs-6'>
          Where your complaint is about an institution, we will help you find its complaints
          procedure and the right contact, and you may use that procedure directly. You can raise a
          complaint with the institution and with us at the same time if the matter involves both.
        </p>
      </PolicySection>

      <PolicySection title='Fair treatment, confidentiality and records'>
        <p className='dark-blue fs-6'>
          Making a genuine complaint will not lead to you being treated less favourably by UHDSS.
          We handle complaints respectfully, objectively, and confidentially as far as is reasonably
          possible, sharing details only with the people who need them to investigate.
        </p>
        <p className='dark-blue fs-6'>
          We keep records of complaints, of what we found, of the decisions we made and of any
          action we took, so that we can check our service quality and meet our legal obligations.
          Those records are handled in line with our privacy policy.
        </p>
      </PolicySection>
    </PolicyPage>
  )
}

export default Complaints
